package org.lennycodes.hldi.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.lennycodes.hldi.data.ResourceDocument
import org.lennycodes.hldi.styles.CategoryBadgeStyle
import org.lennycodes.hldi.styles.DownloadButtonStyle
import org.lennycodes.hldi.styles.ResourceCardStyle
import org.lennycodes.hldi.styles.ViewButtonStyle

/**
 * Category → accent color mapping.
 * Swap these Color.rgb() literals for HLDITheme.Colors tokens if you have
 * matching named colors already defined — kept as raw values here since I
 * don't have your theme file to confirm exact token names.
 */
private data class CategoryAccent(val solid: CSSColorValue, val tint: CSSColorValue)
object ResourceCategories {
    const val ANNUAL_REPORTS = "Annual Reports"
    const val POLICIES = "Policies & Guidelines"
    const val PROJECT_REPORTS = "Project Reports"
    const val RESEARCH = "Research & Publications"
    const val FINANCIAL = "Financial Reports"
    const val REPORTS = "Reports"
}
private fun accentFor(category: String): CategoryAccent = when (category) {

    ResourceCategories.ANNUAL_REPORTS -> CategoryAccent(
        solid = Color.rgb(30, 64, 110),
        tint = Color.rgba(30, 64, 110, 0.10f)
    )

    ResourceCategories.POLICIES -> CategoryAccent(
        solid = Color.rgb(31, 122, 116),
        tint = Color.rgba(31, 122, 116, 0.10f)
    )

    ResourceCategories.PROJECT_REPORTS -> CategoryAccent(
        solid = Color.rgb(184, 134, 11),
        tint = Color.rgba(184, 134, 11, 0.12f)
    )

    ResourceCategories.RESEARCH -> CategoryAccent(
        solid = Color.rgb(46, 106, 68),
        tint = Color.rgba(46, 106, 68, 0.10f)
    )

    ResourceCategories.FINANCIAL -> CategoryAccent(
        solid = Color.rgb(140, 44, 62),
        tint = Color.rgba(140, 44, 62, 0.10f)
    )

    else -> CategoryAccent(
        solid = Color.rgb(51, 65, 85),
        tint = Color.rgba(51, 65, 85, 0.10f)
    )
}



@Composable
fun ResourceCard(
    resource: ResourceDocument,
    onView: () -> Unit,
    onDownload: () -> Unit
) {
    val accent = accentFor(resource.category)

    Div(attrs = Modifier.padding(28.px).then( ResourceCardStyle.toModifier()).toAttrs()) {

        // Top accent bar — reads as a folder-tab, ties each card to its category
        Div(
            attrs = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("top", "0")
                    property("left", "0")
                    property("right", "0")
                }
                .height(4.px)
                .backgroundColor(accent.solid)
                .toAttrs()
        ) {}

        // Folded-corner detail — the one signature flourish, evokes a physical document
        Div(
            attrs = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("top", "0")
                    property("right", "0")
                    property("width", "0")
                    property("height", "0")
                    property("border-style", "solid")
                    property("border-width", "0 26px 26px 0")
                    property("border-color", "transparent ${accent.tint} transparent transparent")
                }
                .toAttrs()
        ) {}

        // Category badge
        Div(
            attrs = Modifier
                .margin(top = 6.px, bottom = 18.px)
                .toAttrs()
        ) {
            Span(
                attrs = CategoryBadgeStyle.toModifier()
                    .backgroundColor(accent.tint)
                    .color(accent.solid)
                    .toAttrs()
            ) {
                Text(resource.category)
            }
        }

        // Title
        H3(
            attrs = Modifier
                .margin(bottom = 10.px)
                .color(Color.rgb(17, 24, 39))
                .styleModifier {
                    property("font-size", "18px")
                    property("line-height", "1.35")
                }
                .toAttrs()
        ) {
            Text(resource.title)
        }

        // Description — clamped to 2 lines so cards stay a uniform height
        P(
            attrs = Modifier
                .margin(bottom = 18.px)
                .color(Color.rgb(90, 99, 115))
                .styleModifier {
                    property("font-size", "14px")
                    property("line-height", "1.6")
                    property("display", "-webkit-box")
                    property("-webkit-line-clamp", "2")
                    property("-webkit-box-orient", "vertical")
                    property("overflow", "hidden")
                }
                .toAttrs()
        ) {
            Text(resource.description)
        }

        // Meta row
        Div(
            attrs = Modifier
                .display(DisplayStyle.Flex)
                .styleModifier { property("align-items", "center") }
                .gap(8.px)
                .margin(bottom = 20.px)
                .color(Color.rgb(140, 148, 163))
                .styleModifier {
                    property("font-size", "12.5px")
                    property("font-weight", "500")
                    property("border-top", "1px solid ${Color.rgba(15, 30, 60, 0.08f)}")
                    property("padding-top", "14px")
                }
                .toAttrs()
        ) {
            Text("Published ${resource.publishedOn}")
        }

        // Actions
        Div(
            attrs = Modifier
                .display(DisplayStyle.Flex)
                .gap(10.px)
                .toAttrs()
        ) {
            org.jetbrains.compose.web.dom.Button(
                attrs = Modifier.padding(topBottom = 11.px, leftRight = 16.px).then( ViewButtonStyle.toModifier())
                    .onClick { onView() }
                    .toAttrs()
            ) {
                Text("View")
            }
            org.jetbrains.compose.web.dom.Button(
                attrs = Modifier   .padding(topBottom = 11.px, leftRight = 16.px).then(DownloadButtonStyle.toModifier())
                    .onClick { onDownload() }
                    .toAttrs()
            ) {
                Text("Download")
            }
        }
    }
}